import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { getCurrentOrgId } from '../../lib/organizationContext'
import { Surface, TextField, Label, InputGroup, TextArea, Select, ListBox, Button } from '@heroui/react'
import { Instagram, Youtube, Twitter, Linkedin, Facebook } from 'lucide-react'
import UserIcon from '~icons/solar/user-bold'
import Mail from '~icons/solar/letter-bold'
import Globe from '~icons/solar/global-bold'
import ArrowRight from '~icons/solar/arrow-right-outline'
import ArrowLeft from '~icons/solar/arrow-left-outline'
import CheckCircle from '~icons/solar/check-circle-bold'

interface ApplicationFormData {
  fullName: string
  email: string
  websiteUrl: string
  companyName: string
  audienceSize: string
  promotionChannels: string[]
  socialMedia: {
    instagram?: string
    youtube?: string
    twitter?: string
    linkedin?: string
    facebook?: string
  }
  promotionStrategy: string
  expectedMonthlySales: string
}

export default function AffiliateApplication() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [formData, setFormData] = useState<ApplicationFormData>({
    fullName: '',
    email: '',
    websiteUrl: '',
    companyName: '',
    audienceSize: '',
    promotionChannels: [],
    socialMedia: {},
    promotionStrategy: '',
    expectedMonthlySales: ''
  })

  // Fetch organization ID on mount
  useEffect(() => {
    const fetchOrgId = async () => {
      try {
        const orgId = await getCurrentOrgId()
        setOrganizationId(orgId)
      } catch (error) {
        console.error('[Application] Error fetching organization ID:', error)
        alert('Error: Could not determine organization. Please try again.')
      }
    }
    fetchOrgId()
  }, [])

  const promotionChannelOptions = [
    'Website/Blog',
    'Email Marketing',
    'Social Media',
    'YouTube',
    'Podcast',
    'Paid Advertising',
    'Content Marketing',
    'Influencer Marketing'
  ]

  const audienceSizeOptions = [
    '0-1,000',
    '1,000-10,000',
    '10,000-50,000',
    '50,000-100,000',
    '100,000-500,000',
    '500,000+'
  ]

  const expectedSalesOptions = [
    '0-10',
    '10-50',
    '50-100',
    '100-500',
    '500+'
  ]

  const handleInputChange = (field: keyof ApplicationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleChannelToggle = (channel: string) => {
    setFormData(prev => ({
      ...prev,
      promotionChannels: prev.promotionChannels.includes(channel)
        ? prev.promotionChannels.filter(c => c !== channel)
        : [...prev.promotionChannels, channel]
    }))
  }

  const handleSocialMediaChange = (platform: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }))
  }

  const validateStep = (stepNumber: number) => {
    switch (stepNumber) {
      case 1:
        return formData.fullName && formData.email && formData.websiteUrl
      case 2:
        return formData.audienceSize && formData.promotionChannels.length > 0
      case 3:
        return formData.promotionStrategy && formData.expectedMonthlySales
      default:
        return true
    }
  }

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateStep(3)) return

    if (!organizationId) {
      alert('Error: Organization not loaded. Please refresh the page.')
      return
    }

    setLoading(true)

    try {
      // Check if this email already has an affiliate or application for this organization
      const { data: existingAffiliate } = await supabase
        .from('affiliates')
        .select(`
          id,
          affiliate_programs!inner (
            id,
            organization_id,
            status
          )
        `)
        .eq('email', formData.email)
        .eq('affiliate_programs.organization_id', organizationId)
        .single()

      if (existingAffiliate) {
        const program = existingAffiliate.affiliate_programs?.[0]
        if (program?.status === 'approved') {
          alert('You already have an active affiliate account with this organization. Please login instead.')
          return
        } else if (program?.status === 'pending') {
          alert('You already have a pending application with this organization.')
          return
        }
      }

      // Check for existing application
      const { data: existingApplication } = await supabase
        .from('affiliate_applications')
        .select('id, status')
        .eq('email', formData.email)
        .eq('organization_id', organizationId)
        .single()

      if (existingApplication) {
        if (existingApplication.status === 'pending') {
          alert('You already have a pending application with this organization. Please wait for admin approval.')
          return
        }
      }

      const { error } = await supabase
        .from('affiliate_applications')
        .insert({
          organization_id: organizationId,
          email: formData.email,
          full_name: formData.fullName,
          website_url: formData.websiteUrl,
          company_name: formData.companyName || null,
          audience_size: formData.audienceSize,
          promotion_channels: formData.promotionChannels,
          social_media: formData.socialMedia,
          promotion_strategy: formData.promotionStrategy,
          expected_monthly_sales: formData.expectedMonthlySales,
          reason: formData.promotionStrategy, // Map to required 'reason' field
          status: 'pending',
          auto_approval_score: calculateAutoApprovalScore()
        })

      if (error) throw error

      setSubmitted(true)
    } catch (error) {
      console.error('Error submitting application:', error)
      alert('Failed to submit application. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const calculateAutoApprovalScore = () => {
    let score = 0

    // Audience size scoring
    const audienceSizes = ['0-1,000', '1,000-10,000', '10,000-50,000', '50,000-100,000', '100,000-500,000', '500,000+']
    score += audienceSizes.indexOf(formData.audienceSize) * 20

    // Multiple channels bonus
    score += formData.promotionChannels.length * 10

    // Social media presence
    score += Object.keys(formData.socialMedia).filter(k => formData.socialMedia[k as keyof typeof formData.socialMedia]).length * 5

    // Website presence
    if (formData.websiteUrl) score += 15

    return Math.min(score, 100)
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Surface variant="default" className="rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success-soft-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Application Submitted!
            </h2>
            <p className="text-muted mb-6">
              Thank you for applying to our affiliate program. We'll review your application and get back to you within 2-3 business days.
            </p>
            <Button className="w-full" onPress={() => navigate('/')}>
              Back to Home
            </Button>
          </Surface>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Join Our Affiliate Program
          </h1>
          <p className="text-muted">
            Start earning commissions by promoting our products
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0
                  ${s <= step ? 'bg-accent text-accent-foreground' : 'bg-surface-secondary text-muted'}
                `}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded-full ${s < step ? 'bg-accent' : 'bg-surface-secondary'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted">
            <span>Personal Info</span>
            <span>Audience & Channels</span>
            <span>Strategy</span>
          </div>
        </div>

        {/* Form */}
        <Surface variant="default" className="rounded-2xl p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">Personal Information</h2>

                <TextField
                  isRequired
                  name="fullName"
                  value={formData.fullName}
                  onChange={(v) => handleInputChange('fullName', v)}
                >
                  <Label>Full Name</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Prefix>
                      <UserIcon className="h-4 w-4" />
                    </InputGroup.Prefix>
                    <InputGroup.Input placeholder="John Doe" />
                  </InputGroup>
                </TextField>

                <TextField
                  isRequired
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(v) => handleInputChange('email', v)}
                >
                  <Label>Email Address</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Prefix>
                      <Mail className="h-4 w-4" />
                    </InputGroup.Prefix>
                    <InputGroup.Input placeholder="john@example.com" />
                  </InputGroup>
                </TextField>

                <TextField
                  isRequired
                  type="url"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={(v) => handleInputChange('websiteUrl', v)}
                >
                  <Label>Website URL</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Prefix>
                      <Globe className="h-4 w-4" />
                    </InputGroup.Prefix>
                    <InputGroup.Input placeholder="https://yourwebsite.com" />
                  </InputGroup>
                </TextField>

                <TextField
                  name="companyName"
                  value={formData.companyName}
                  onChange={(v) => handleInputChange('companyName', v)}
                >
                  <Label>Company Name (Optional)</Label>
                  <InputGroup variant="secondary">
                    <InputGroup.Input placeholder="Your Company LLC" />
                  </InputGroup>
                </TextField>
              </div>
            )}

            {/* Step 2: Audience & Channels */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">Audience & Channels</h2>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="audienceSize">
                    Audience Size <span className="text-danger">*</span>
                  </Label>
                  <Select
                    id="audienceSize"
                    aria-label="Audience Size"
                    variant="secondary"
                    value={formData.audienceSize}
                    onChange={(v) => handleInputChange('audienceSize', v as string)}
                    className="w-full"
                  >
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {audienceSizeOptions.map(size => (
                          <ListBox.Item key={size} id={size} textValue={size}>
                            {size}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>

                <div>
                  <Label>
                    Promotion Channels <span className="text-danger">*</span>{' '}
                    <span className="text-muted font-normal">(Select all that apply)</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {promotionChannelOptions.map(channel => (
                      <button
                        key={channel}
                        type="button"
                        onClick={() => handleChannelToggle(channel)}
                        className={`
                          px-4 py-3 rounded-xl border text-sm font-medium transition-colors text-left
                          ${formData.promotionChannels.includes(channel)
                            ? 'border-accent bg-accent-soft text-accent-soft-foreground'
                            : 'border-black/10 dark:border-white/10 text-foreground hover:border-black/20 dark:hover:border-white/20'
                          }
                        `}
                      >
                        {channel}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Social Media Profiles (Optional)</Label>
                  <div className="space-y-3 mt-2">
                    {[
                      { name: 'instagram', icon: Instagram, placeholder: 'instagram.com/yourprofile' },
                      { name: 'youtube', icon: Youtube, placeholder: 'youtube.com/@yourchannel' },
                      { name: 'twitter', icon: Twitter, placeholder: 'twitter.com/yourhandle' },
                      { name: 'linkedin', icon: Linkedin, placeholder: 'linkedin.com/in/yourprofile' },
                      { name: 'facebook', icon: Facebook, placeholder: 'facebook.com/yourpage' }
                    ].map(({ name, icon: Icon, placeholder }) => (
                      <InputGroup key={name} variant="secondary">
                        <InputGroup.Prefix>
                          <Icon className="h-4 w-4" />
                        </InputGroup.Prefix>
                        <InputGroup.Input
                          type="url"
                          value={formData.socialMedia[name as keyof typeof formData.socialMedia] || ''}
                          onChange={(e) => handleSocialMediaChange(name, e.target.value)}
                          placeholder={placeholder}
                        />
                      </InputGroup>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Strategy */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground mb-4">Promotion Strategy</h2>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="promotionStrategy">
                    How will you promote our products? <span className="text-danger">*</span>
                  </Label>
                  <TextArea
                    id="promotionStrategy"
                    variant="secondary"
                    fullWidth
                    rows={6}
                    value={formData.promotionStrategy}
                    onChange={(e) => handleInputChange('promotionStrategy', e.target.value)}
                    placeholder="Describe your promotion strategy, content types, target audience, etc."
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="expectedMonthlySales">
                    Expected Monthly Sales <span className="text-danger">*</span>
                  </Label>
                  <Select
                    id="expectedMonthlySales"
                    aria-label="Expected Monthly Sales"
                    variant="secondary"
                    value={formData.expectedMonthlySales}
                    onChange={(v) => handleInputChange('expectedMonthlySales', v as string)}
                    className="w-full"
                  >
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {expectedSalesOptions.map(sales => (
                          <ListBox.Item key={sales} id={sales} textValue={`${sales} sales per month`}>
                            {sales} sales per month
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button variant="outline" type="button" onPress={handleBack}>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              ) : (
                <Button variant="outline" type="button" onPress={() => navigate('/')}>
                  Cancel
                </Button>
              )}

              {step < 3 ? (
                <Button type="button" onPress={handleNext} isDisabled={!validateStep(step)}>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button type="submit" isDisabled={loading || !validateStep(3)}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                  {!loading && <CheckCircle className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </form>
        </Surface>
      </div>
    </div>
  )
}
